import { Component, EventEmitter, Injector, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { AbstractComponent, CountryModel, UtilHelper, WA_TEMPLATE_LIMITS, WaTemplateButtonType } from 'lib-trend-core';
import { TemplateFormEngine } from './template-form-engine';

/**
 * One button row.
 *
 * This is a component and NOT an `<ng-template>` rendered with `*ngTemplateOutlet`, which is how it
 * was first written and why the fields silently stopped saving: an embedded view resolves its
 * directive injector from where the template is DECLARED, not from where it is inserted. The
 * template sat outside `[formGroup]`, so every `formControlName` inside it had no parent form to
 * bind to — the inputs rendered and accepted text that never reached the control, which is why the
 * character counters read 0 while text was on screen.
 *
 * A component takes the group as an input and re-binds it with `[formGroup]`, so the binding is
 * explicit and cannot depend on where the markup happens to be written.
 *
 * SPEC-2026-08-whatsapp-cloud-api-template-builder — RF-35 … RF-46, RF-50.
 */
@Component({
    selector: 'template-button-row',
    templateUrl: './template-button-row.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TemplateButtonRowComponent extends AbstractComponent {

  @Input() engine: TemplateFormEngine;
  /** Position of this button in the whole `buttons` FormArray. */
  @Input() index: number;
  /** Indexes of this button's family, in order — the reorder controls work inside it. */
  @Input() indexes: Array<number> = [];
  /** Position of this button inside `indexes`. */
  @Input() position: number = 0;

  @Output() removed = new EventEmitter<number>();

  readonly limits = WA_TEMPLATE_LIMITS;
  readonly countries: Array<CountryModel> = UtilHelper.getCountries();

  readonly buttonTypes: Array<{ value: WaTemplateButtonType, label: string }> = [
    { value: 'QUICK_REPLY', label: 'channel.templateBuilder.buttonCustom' },
    { value: 'URL', label: 'channel.templateBuilder.buttonUrl' },
    { value: 'VOICE_CALL', label: 'channel.templateBuilder.buttonVoiceCall' },
    { value: 'PHONE_NUMBER', label: 'channel.templateBuilder.buttonPhoneNumber' },
    { value: 'COPY_CODE', label: 'channel.templateBuilder.buttonCopyCode' },
  ];

  readonly urlTypes: Array<{ value: string, label: string }> = [
    { value: 'STATIC', label: 'channel.templateBuilder.urlTypeStatic' },
    { value: 'DYNAMIC', label: 'channel.templateBuilder.urlTypeDynamic' },
  ];

  /** RF-38 — the two Custom sub-types. */
  readonly customSubTypes: Array<{ value: string, label: string }> = [
    { value: 'CUSTOM', label: 'channel.templateBuilder.subTypeCustom' },
    { value: 'PRECONFIGURED_REPLY', label: 'channel.templateBuilder.subTypePreconfiguredReply' },
  ];

  /** Documentation Meta points to for enabling calls (RF-42). */
  readonly voiceCallDocumentationUrl = 'https://developers.facebook.com/docs/whatsapp/cloud-api/calling';

  constructor(injector: Injector) {
    super(injector);
  }

  get group(): FormGroup {
    return this.engine?.buttons?.at(this.index) as FormGroup;
  }

  get type(): WaTemplateButtonType {
    return this.group?.get('type')?.value;
  }

  /**
   * A Pix template has one button, of one type, and it cannot be reordered or removed — so the row
   * drops the type select, the reorder arrows and the remove action rather than showing controls
   * that would all refuse.
   */
  get locked(): boolean {
    return !!this.engine?.pixMode;
  }

  /**
   * ORDER_DETAILS is deliberately absent from `buttonTypes`: it exists only inside a template
   * created with `displayFormat: ORDER_DETAILS`, so it must never be offered as a type to switch to.
   * Its label is resolved separately, for display only.
   */
  get typeLabel(): string {
    return this.type === 'ORDER_DETAILS'
      ? 'channel.templateBuilder.buttonOrderDetails'
      : this.buttonTypeLabel(this.type);
  }

  /**
   * Types whose label the provider fixes and refuses to see changed — the field is shown so the
   * operator knows what the customer will read, but it is not theirs to edit.
   */
  get hasFixedLabel(): boolean {
    return this.type === 'COPY_CODE' || this.type === 'ORDER_DETAILS';
  }

  /**
   * Only two action labels are long enough to need extra room — "Ligar no WhatsApp" and
   * "Copiar código da oferta". Giving every type the same generous width would shrink the label
   * field for no reason, so the width follows the label instead of being uniform.
   */
  private get hasLongTypeLabel(): boolean {
    return this.type === 'VOICE_CALL' || this.type === 'COPY_CODE' || this.type === 'ORDER_DETAILS';
  }

  get typeColumnClass(): string {
    return this.hasLongTypeLabel ? 'col-md-6' : 'col-md-4';
  }

  /**
   * The label field takes whatever the rest of the first row leaves, so it always adds up to 12:
   * a Custom button also carries the sub-type there, which is why it gets a third rather than
   * two thirds.
   */
  get labelColumnClass(): string {
    if (this.type === 'QUICK_REPLY') {
      return 'col-md-4';
    }
    return this.hasLongTypeLabel ? 'col-md-6' : 'col-md-8';
  }

  value(control: string): string {
    return this.group?.get(control)?.value ?? '';
  }

  /** A required control the operator has already been to, and left empty. */
  isMissing(control: string): boolean {
    const field = this.group?.get(control);
    return !!field && field.hasError('required') && (field.dirty || field.touched);
  }

  buttonTypeLabel(type: WaTemplateButtonType): string {
    return this.buttonTypes.find((item) => item.value === type)?.label ?? '';
  }

  /** RF-50 — refused when the target type is already at its maximum, and the select is put back. */
  changeType(type: WaTemplateButtonType, select: MatSelect): void {
    const previous: WaTemplateButtonType = this.type;
    if (!this.engine.changeButtonType(this.index, type)) {
      select.value = previous;
      this.alertService.error(this.t('channel.templateBuilder.errorButtonTypeFull', {
        type: this.t(this.buttonTypeLabel(type)),
        max: this.engine.maxOfType(type),
      }));
    }
  }

  /** RNF-07 — the CDK drag handle is pointer-only, so reordering also has a keyboard path. */
  canMove(delta: number): boolean {
    const target = this.position + delta;
    return target >= 0 && target < this.indexes.length;
  }

  move(delta: number): void {
    if (this.canMove(delta)) {
      this.engine.moveButton(this.indexes[this.position], this.indexes[this.position + delta]);
    }
  }

  remove(): void {
    this.removed.emit(this.index);
  }
}
